import * as THREE from 'three'
import { SceneAuxiliaryObjectRegistry } from '../../scene-object'
import { shapeCircle } from 'three/tsl'
import { SW_HOVER_COLOR, SW_SELECTED_COLOR } from '../render/sw-theme'
import type { ImportedModelResolvedTarget } from './ImportedAssemblyPartTargetResolver'
import {
  addNonSelectableObject3DToParent,
  addObject3DToParent,
  moveObject3DChildToIndex,
} from '../../utils/scene-object/addObject3DToParent'
import { CameraSpriteNodeMaterial } from '../../material/CameraSpriteNodeMaterial'
import { ImportedModelOverlayGeometrySnapshot } from './ImportedModelOverlayGeometrySnapshot'

type ImportedModelOverlayChannelKind = 'hover' | 'selected' | 'commandSelected'
type ImportedModelGeometryGroup = THREE.BufferGeometry['groups'][number]

interface ImportedModelOverlayChannelState {
  /** 通道类型，用于区分 hover 与命令态高亮的资源所有权策略。 */
  kind: ImportedModelOverlayChannelKind
  /** 通道宿主，用于复用 hover/selected 两个固定 overlay 通道。 */
  channel: THREE.Group
  /** 当前通道挂出的 overlay 列表。 */
  overlays: Array<THREE.Mesh | THREE.Line | THREE.Sprite>
  /** 只索引当前活动目标；同一源的不同 primitive/出现项分别持有覆盖层。 */
  bindings: Map<THREE.Object3D, ImportedModelOverlayBinding[]>
  /** 当前目标集合更新序号。 */
  generation: number
  /** 通道共享面覆盖材质模板。 */
  surfaceMaterialTemplate: THREE.MeshBasicMaterial
  /** 通道共享线覆盖材质模板。 */
  lineMaterialTemplate: THREE.LineBasicMaterial
  pointMaterialTemplate: CameraSpriteNodeMaterial
  /** 通道当前挂出的包围盒线框（若无则为 null）。 */
  boundingBoxHelper: THREE.Box3Helper | null
  /** 当前通道渲染顺序。 */
  renderOrder: number
}

/**
 * imported-model 普通 overlay 渲染器。
 *
 * 设计说明：
 * 1. 固定维护 hover / selected / commandSelected 三个通道，通道与未变化目标的覆盖层复用；
 * 2. 清理通道时只移除 overlay，并仅释放本 renderer 自己创建的临时资源；
 * 3. Hover 完整几何可引用源数据，选中几何继续独立克隆；clear/dispose 只释放自有资源。
 */
export class ImportedModelOverlayRenderer {
  private static readonly OVERLAY_GEOMETRY_DISPOSABLE_KEY = 'disposeImportedModelOverlayGeometry'

  private readonly scene: THREE.Scene
  private readonly hoverChannelState: ImportedModelOverlayChannelState
  private readonly selectedChannelState: ImportedModelOverlayChannelState
  private readonly commandSelectedChannelState: ImportedModelOverlayChannelState
  /** 渲染前读取实例 source Mesh 有效世界矩阵时复用的目标矩阵。 */
  private readonly matrixSourceWorldMatrix = new THREE.Matrix4()
  /** Hover 包围盒的唯一数值载体；更新范围不重新分配 Box3 或 GPU 线框资源。 */
  private readonly hoverBoundingBox = new THREE.Box3()
  private disposed = false
  /** 清理后替换矩阵回调，解除绑定引用且避免逐项创建空闭包。 */
  private readonly clearedRenderCallback = (): void => undefined

  /**
   * @param scene imported-model 所在场景。
   * @param copyObjectWorldMatrix 特殊对象有效世界矩阵读取端口；返回 false 时使用普通父链。
   */
  public constructor(
    scene: THREE.Scene,
    private readonly copyObjectWorldMatrix?: (
      object: THREE.Object3D,
      targetMatrix: THREE.Matrix4,
    ) => boolean,
  ) {
    this.scene = scene
    this.hoverChannelState = this.createChannelState('hover')
    this.selectedChannelState = this.createChannelState('selected')
    this.commandSelectedChannelState = this.createChannelState('commandSelected')
  }

  /**
   * 设置 hover overlay。
   *
   * @param targets 待渲染目标列表。
   */
  public setHover(targets: ImportedModelResolvedTarget[]): void {
    this.ensureNotDisposed()
    this.renderChannel(this.hoverChannelState, targets)
  }

  /**
   * 用包围盒线框表达 hover 高亮。
   * 说明：
   * 1. 针对 part/assembly 等"多 mesh 聚合"目标，逐 mesh 贴 overlay 既重又视觉分散，改用单个世界 AABB 线框表达；
   * 2. 与 overlay mesh 共用同一个 hover 通道 group，调用时自动互斥。
   *
   * @param targetObject 计算包围盒的目标对象。
   */
  public setHoverBoundingBox(targetObject: THREE.Object3D): void {
    this.ensureNotDisposed()
    this.renderBoundingBoxTargets(this.hoverChannelState, targetObject)
  }

  /**
   * 用一组对象的合并包围盒表达 hover 高亮。
   *
   * 设计说明：
   * 1. 设计树 Body 节点可能是虚拟语义节点，真实显示目标由多个运行时 mesh 共同组成；
   * 2. renderer 统一负责 bbox 计算与通道生命周期，调用方不需要临时创建 Group 或把对象重挂到场景；
   * 3. 空目标会清空 hover 通道，避免旧包围盒残留。
   *
   * @param targetObjects 需要参与包围盒计算的运行时对象。
   */
  public setHoverBoundingBoxTargets(targetObjects: readonly THREE.Object3D[]): void {
    this.ensureNotDisposed()
    this.renderBoundingBoxTargets(this.hoverChannelState, targetObjects)
  }

  /**
   * 清空 hover overlay。
   */
  public clearHover(): void {
    this.clearChannel(this.hoverChannelState)
  }

  /**
   * 设置 selected overlay。
   *
   * @param targets 待渲染目标列表。
   */
  public setSelected(targets: ImportedModelResolvedTarget[]): void {
    this.ensureNotDisposed()
    this.renderChannel(this.selectedChannelState, targets)
  }

  /**
   * 清空 selected overlay。
   */
  public clearSelected(): void {
    this.clearChannel(this.selectedChannelState)
  }

  /**
   * 设置命令态 selected overlay。
   *
   * 设计说明：
   * Mate PropertyManager 里的“配合选择”属于命令态多选，不应被普通场景 selected / clear selected
   * 事件复用或清空。单独通道可以让普通选择链路继续保持默认行为，同时保证 Mate 已吸收实体持续高亮。
   *
   * @param targets 待渲染目标列表。
   */
  public setCommandSelected(targets: ImportedModelResolvedTarget[]): void {
    this.ensureNotDisposed()
    this.renderChannel(this.commandSelectedChannelState, targets)
  }

  /**
   * 清空命令态 selected overlay。
   */
  public clearCommandSelected(): void {
    this.clearChannel(this.commandSelectedChannelState)
  }

  /**
   * 释放 renderer 自身资源。
   */
  public dispose(): void {
    if (this.disposed) {
      return
    }

    this.clearChannel(this.hoverChannelState)
    this.clearChannel(this.selectedChannelState)
    this.clearChannel(this.commandSelectedChannelState)
    this.hoverChannelState.surfaceMaterialTemplate.dispose()
    this.selectedChannelState.surfaceMaterialTemplate.dispose()
    this.commandSelectedChannelState.surfaceMaterialTemplate.dispose()
    this.hoverChannelState.lineMaterialTemplate.dispose()
    this.selectedChannelState.lineMaterialTemplate.dispose()
    this.commandSelectedChannelState.lineMaterialTemplate.dispose()
    this.hoverChannelState.pointMaterialTemplate.dispose()
    this.selectedChannelState.pointMaterialTemplate.dispose()
    this.commandSelectedChannelState.pointMaterialTemplate.dispose()
    this.scene.remove(this.hoverChannelState.channel)
    this.scene.remove(this.selectedChannelState.channel)
    this.scene.remove(this.commandSelectedChannelState.channel)
    this.disposed = true
  }

  /**
   * 创建固定通道状态。
   *
   * @param kind 通道类型。
   * @returns 通道状态。
   */
  private createChannelState(kind: ImportedModelOverlayChannelKind): ImportedModelOverlayChannelState {
    const channel = new THREE.Group()
    // 通道内的面、线、点和包围盒统一继承辅助身份，不能触发组件结构失效。
    SceneAuxiliaryObjectRegistry.markRoot(channel)
    const selectedLike = kind === 'selected' || kind === 'commandSelected'
    channel.name = kind === 'commandSelected'
      ? 'imported-model-command-selected-overlay-channel'
      : `imported-model-${kind}-overlay-channel`
    channel.visible = false
    channel.userData.isImportedModelOverlayChannel = true

    addObject3DToParent(channel, this.scene)

    return {
      kind,
      channel,
      overlays: [],
      bindings: new Map(),
      generation: 0,
      surfaceMaterialTemplate: new THREE.MeshBasicMaterial({
        color: kind === 'hover' ? SW_HOVER_COLOR : SW_SELECTED_COLOR,
        transparent: true,
        opacity: kind === 'hover' ? 0.18 : 0.28,
        depthWrite: false,
        depthTest: false,
        side: THREE.DoubleSide,
        polygonOffset: true,
        polygonOffsetFactor: -2,
        polygonOffsetUnits: -2,
      }),
      lineMaterialTemplate: new THREE.LineBasicMaterial({
        color: kind === 'hover' ? SW_HOVER_COLOR : SW_SELECTED_COLOR,
        linewidth: 1,
        transparent: true,
        opacity: 0.95,
        depthTest: false,
      }),
      pointMaterialTemplate: ImportedModelOverlayRenderer.createPointOverlaySpriteMaterial(
        kind === 'hover' ? SW_HOVER_COLOR : SW_SELECTED_COLOR,
      ),
      boundingBoxHelper: null,
      renderOrder: kind === 'hover' ? 7 : selectedLike && kind === 'commandSelected' ? 6 : 5,
    }
  }

  /**
   * 按当前目标范围更新唯一 Hover 包围盒，同时排除本通道自身的显示代理。
   *
   * 单目标不包装临时数组；仅从其它视觉模式切入时清理覆盖层。
   * 查询整个 Scene 等包含当前高亮通道的范围时，临时移开自有 helper，
   * 防止旧范围递归参与新范围计算；不释放 GPU 资源，也不重挂任何模型对象。
   *
   * @param channelState 通道状态。
   * @param targetObjects 单个目标或目标集合。
   */
  private renderBoundingBoxTargets(
    channelState: ImportedModelOverlayChannelState,
    targetObjects: THREE.Object3D | readonly THREE.Object3D[],
  ): void {
    if (channelState.overlays.length > 0) this.clearChannel(channelState)
    const singleTarget = targetObjects instanceof THREE.Object3D ? targetObjects : null
    const targetList = targetObjects instanceof THREE.Object3D ? null : targetObjects
    const helper = channelState.boundingBoxHelper
    let helperOverlapsTarget = false
    if (helper?.parent === channelState.channel) {
      // 高亮通道的祖先链很短，不为判定相交遍历模型子树。
      let ancestor: THREE.Object3D | null = channelState.channel
      while (ancestor) {
        if (ancestor === singleTarget || targetList?.includes(ancestor)) {
          helperOverlapsTarget = true
          break
        }
        ancestor = ancestor.parent
      }
    }
    try {
      if (helperOverlapsTarget) helper!.removeFromParent()
      this.hoverBoundingBox.makeEmpty()
      const targetCount = targetList?.length ?? 1
      for (let index = 0; index < targetCount; index += 1) {
        const targetObject = targetList ? targetList[index]! : singleTarget!
        // Box3 会更新目标及其后代，但不会更新祖先；只补齐当前父链。
        targetObject.updateWorldMatrix(true, false)
        this.hoverBoundingBox.expandByObject(targetObject)
      }
    } finally {
      // 对象更新或监听器可能抛错；仍须恢复本 renderer 保留的 helper，而不是遗留游离资源。
      if (helperOverlapsTarget && !this.disposed && channelState.boundingBoxHelper === helper) {
        addNonSelectableObject3DToParent(helper!, channelState.channel)
      }
    }
    this.renderBoundingBoxFromBox(channelState, this.hoverBoundingBox)
  }

  /**
   * 将已计算好的世界包围盒挂到指定 overlay 通道。
   *
   * @param channelState 通道状态。
   * @param box 世界包围盒。
   */
  private renderBoundingBoxFromBox(
    channelState: ImportedModelOverlayChannelState,
    box: THREE.Box3,
  ): void {
    if (box.isEmpty()) {
      // 目标已被删除或收回时必须释放旧 helper，不能让上一次的有效范围继续显示。
      this.clearChannel(channelState)
      return
    }

    if (channelState.boundingBoxHelper) {
      channelState.boundingBoxHelper.box.copy(box)
      channelState.channel.visible = true
      return
    }

    const color = channelState.lineMaterialTemplate.color.getHex()
    const helper = new THREE.Box3Helper(box, color)
    const helperMaterial = helper.material
    if (Array.isArray(helperMaterial)) {
      helperMaterial.forEach((material) => {
        material.depthTest = false
        material.depthWrite = false
        material.transparent = true
        material.opacity = 1
      })
    } else {
      helperMaterial.depthTest = false
      helperMaterial.depthWrite = false
      helperMaterial.transparent = true
      helperMaterial.opacity = 1
    }
    helper.renderOrder = channelState.renderOrder + 1
    helper.userData.isImportedModelOverlay = true
    helper.userData.isImportedModelOverlayHover = channelState === this.hoverChannelState
    helper.userData.isImportedModelOverlayBoundingBox = true
    helper.userData.skipSelection = true

    addNonSelectableObject3DToParent(helper, channelState.channel)
    channelState.boundingBoxHelper = helper
    channelState.channel.visible = true
  }

  /**
   * 渲染指定通道。
   *
   * @param channelState 通道状态。
   * @param targets 高亮目标。
   */
  private renderChannel(
    channelState: ImportedModelOverlayChannelState,
    targets: ImportedModelResolvedTarget[],
  ): void {
    if (channelState.boundingBoxHelper) this.clearChannel(channelState)
    const generation = ++channelState.generation
    channelState.overlays.length = 0
    try {
      for (const target of targets) {
        let bindings = channelState.bindings.get(target.object)
        let binding = bindings?.find(candidate => candidate.generation !== generation
          && this.matchesPrimitiveIndices(candidate.primitiveIndices, target.primitiveIndices)
          && candidate.geometrySnapshot?.matches(target.object.geometry))
        if (!binding) {
          binding = this.createOverlayBinding(channelState, target, generation) ?? undefined
          if (!binding) continue
          if (!bindings) {
            bindings = []
            channelState.bindings.set(target.object, bindings)
          }
          // 先登记所有权，再更新矩阵和挂载；外部矩阵端口失败时也能清理新资源。
          bindings.push(binding)
          addNonSelectableObject3DToParent(binding.overlay, channelState.channel)
          this.bindOverlayMatrixToTarget(binding)
        } else {
          binding.target = target
          binding.generation = generation
          binding.syncMatrix?.()
        }
        channelState.overlays.push(binding.overlay)
      }

      // 只释放本次已离开的目标；其余对象、几何和材质继续复用。
      for (const [object, bindings] of channelState.bindings) {
        for (let index = bindings.length - 1; index >= 0; index--) {
          if (bindings[index].generation === generation) continue
          this.releaseOverlayBinding(bindings[index])
          bindings.splice(index, 1)
        }
        if (bindings.length === 0) channelState.bindings.delete(object)
      }
      // 保持输入目标的透明排序，不通过重挂载触发整场景成员失效。
      for (let index = 0; index < channelState.overlays.length; index++) {
        moveObject3DChildToIndex(channelState.channel, channelState.overlays[index], index)
      }
      channelState.channel.visible = channelState.overlays.length > 0
    } catch (error) {
      this.clearChannel(channelState)
      throw error
    }
  }

  /**
   * 创建一个由当前通道拥有的覆盖层，不复制模型节点或改变原实例批次。
   * @param channelState 目标通道。
   * @param target 当前精确目标。
   * @param generation 本次目标集合序号。
   * @returns 可复用绑定；不支持的对象返回 null。
   */
  private createOverlayBinding(
    channelState: ImportedModelOverlayChannelState,
    target: ImportedModelResolvedTarget,
    generation: number,
  ): ImportedModelOverlayBinding | null {
    let overlay: THREE.Mesh | THREE.Line | THREE.Sprite
    if (target.object instanceof THREE.Line && !(target.object instanceof THREE.LineSegments)) {
      overlay = new THREE.Line(target.object.geometry, channelState.lineMaterialTemplate.clone())
      overlay.renderOrder = channelState.renderOrder + 1
    } else if (target.object instanceof THREE.Sprite) {
      // 使用相同圆形 TSL 配方独立构造，不经 clone 复制节点和相机订阅。
      const pointMaterial = ImportedModelOverlayRenderer.createPointOverlaySpriteMaterial(channelState.pointMaterialTemplate.color)
      // 材质在相机已切换后创建时也要继承当前状态，不能等下一次投影事件才对齐尺寸。
      pointMaterial.isPerspectiveCameraUniform.value = channelState.pointMaterialTemplate.isPerspectiveCameraUniform.value
      overlay = new THREE.Sprite(pointMaterial)
      overlay.renderOrder = channelState.renderOrder + 2
      overlay.position.copy(target.object.position)
      overlay.scale.copy(target.object.scale)
    } else if (target.object instanceof THREE.Mesh && target.object.geometry instanceof THREE.BufferGeometry) {
      const geometry = this.createOverlayGeometry(channelState, target.object.geometry, target.primitiveIndices)
      overlay = new THREE.Mesh(geometry, channelState.surfaceMaterialTemplate.clone())
      overlay.renderOrder = channelState.renderOrder
      if (geometry !== target.object.geometry) {
        overlay.userData[ImportedModelOverlayRenderer.OVERLAY_GEOMETRY_DISPOSABLE_KEY] = true
      }
    } else {
      return null
    }
    overlay.matrixAutoUpdate = false
    overlay.userData.isImportedModelOverlay = true
    overlay.userData.isImportedModelOverlayHover = channelState === this.hoverChannelState
    overlay.userData.skipSelection = true
    return {
      target, overlay, generation,
      primitiveIndices: target.primitiveIndices?.slice(),
      geometrySnapshot: new ImportedModelOverlayGeometrySnapshot(target.object.geometry),
    }
  }

  /**
   * 比较切片身份；保留原有顺序/重复项语义，不通过排序或去重改变覆盖范围。
   * @param previous 已创建覆盖层的 primitive 快照。
   * @param current 当前解析出的 primitive 列表。
   * @returns 是否可继续校验同一几何绑定。
   */
  private matchesPrimitiveIndices(previous: readonly number[] | undefined, current: readonly number[] | undefined): boolean {
    if ((previous?.length ?? 0) !== (current?.length ?? 0)) return false
    for (let index = 0; index < (previous?.length ?? 0); index++) if (previous![index] !== current![index]) return false
    return true
  }

  /** @param binding 已离开通道的绑定；仅延迟释放覆盖层自己拥有的资源。 */
  private releaseOverlayBinding(binding: ImportedModelOverlayBinding): void {
    const overlay = binding.overlay
    overlay.onBeforeRender = this.clearedRenderCallback
    binding.target = null
    binding.geometrySnapshot = null
    binding.syncMatrix = undefined
    overlay.removeFromParent()
    const geometries = overlay instanceof THREE.Mesh
      && overlay.userData?.[ImportedModelOverlayRenderer.OVERLAY_GEOMETRY_DISPOSABLE_KEY] ? [overlay.geometry] : []
    this.scheduleOverlayResourcesDisposal(geometries, this.collectOverlayMaterials(overlay))
  }

  /** @param channelState 清空的通道；不保留曾访问目标的引用。 */
  private clearChannel(channelState: ImportedModelOverlayChannelState): void {
    for (const bindings of channelState.bindings.values()) for (const binding of bindings) this.releaseOverlayBinding(binding)
    channelState.bindings.clear()
    const helper = channelState.boundingBoxHelper
    if (helper) {
      helper.removeFromParent()
      helper.geometry.dispose()
      const helperMaterial = helper.material
      if (Array.isArray(helperMaterial)) helperMaterial.forEach(material => material.dispose())
      else helperMaterial?.dispose()
    }
    channelState.overlays.length = 0
    channelState.boundingBoxHelper = null
    channelState.channel.visible = false
  }

  /**
   * 创建 overlay 使用的 geometry。
   *
   * 设计说明：
   * 1. commandSelected / selected 会在 Mate 预览、设计树选择和普通选择切换中频繁清理；
   * 2. 这些通道不能复用源 mesh.geometry，否则资源释放策略很难区分源几何和 overlay 几何；
   * 3. hover 高频路径仍允许在无 primitive 裁剪时复用源几何，避免鼠标移动中克隆大网格。
   *
   * @param channelState 当前通道状态。
   * @param geometry 源几何。
   * @param primitiveIndices primitive 子集。
   * @returns overlay 几何。
   */
  private createOverlayGeometry(
    channelState: ImportedModelOverlayChannelState,
    geometry: THREE.BufferGeometry,
    primitiveIndices?: number[],
  ): THREE.BufferGeometry {
    const subsetGeometry = this.createPrimitiveSubsetGeometry(geometry, primitiveIndices)
    if (subsetGeometry) {
      return subsetGeometry
    }

    return channelState.kind === 'hover'
      ? geometry
      : geometry.clone()
  }

  /**
   * 将 overlay 的矩阵绑定到源目标。
   *
   * 设计说明：
   * 1. 配合预览和零部件拖动会在命令态高亮已经创建后继续修改组件 transform；
   * 2. 如果 overlay 只复制一次 world matrix，下一帧就会出现蓝色高亮面停在旧位置；
   * 3. 这里复用 three.js 的 `onBeforeRender` 生命周期刷新矩阵，不在渲染循环里创建新对象，
   *    并在 clear/dispose 时解除回调，避免 overlay 持有已回收的 FeatureFace/Line/Point。
   *
   * @param binding 当前通道拥有的绑定；后续更新只替换目标描述，不重建回调。
   */
  private bindOverlayMatrixToTarget(
    binding: ImportedModelOverlayBinding,
  ): void {
    const overlay = binding.overlay
    const syncMatrix = (): void => {
      const target = binding.target
      if (!target) return
      const matrixSourceObject = target.matrixSourceObject
      const localMatrixToSource = target.localMatrixToSource
      if (matrixSourceObject && localMatrixToSource) {
        /**
         * 命令态拓扑代理的父链可能在普通选择清理或 pick host 保留时变化。
         * 因此优先使用创建 overlay 时记录的稳定矩阵源，保证蓝色面始终跟随真实组件。
         */
        if (!this.copyObjectWorldMatrix?.(matrixSourceObject, this.matrixSourceWorldMatrix)) {
          matrixSourceObject.updateWorldMatrix(true, false)
          this.matrixSourceWorldMatrix.copy(matrixSourceObject.matrixWorld)
        }
        overlay.matrix.multiplyMatrices(this.matrixSourceWorldMatrix, localMatrixToSource)
        overlay.matrixWorld.copy(overlay.matrix)
        return
      }

      if (target.object.parent) {
        target.object.updateWorldMatrix(true, false)
        overlay.matrix.copy(target.object.matrixWorld)
        overlay.matrixWorld.copy(target.object.matrixWorld)
        return
      }

      const fallbackMatrix = target.worldMatrix ?? target.object.matrixWorld
      overlay.matrix.copy(fallbackMatrix)
      overlay.matrixWorld.copy(fallbackMatrix)
    }

    binding.syncMatrix = syncMatrix
    overlay.onBeforeRender = syncMatrix
    syncMatrix()
  }

  /**
   * 为 primitive 子集创建局部 geometry。
   *
   * @param geometry 原始几何。
   * @param primitiveIndices primitive 下标。
   * @returns 新 geometry；若无需子集裁剪则返回 `null`。
   */
  private createPrimitiveSubsetGeometry(
    geometry: THREE.BufferGeometry,
    primitiveIndices?: number[],
  ): THREE.BufferGeometry | null {
    if (!Array.isArray(primitiveIndices) || primitiveIndices.length === 0) {
      return null
    }

    const groups = geometry.groups
    if (groups.length === 0) {
      return primitiveIndices.includes(0) ? geometry.clone() : null
    }

    const selectedGroups = primitiveIndices
      .filter((primitiveIndex) => Number.isInteger(primitiveIndex) && primitiveIndex >= 0)
      .map((primitiveIndex) => groups[primitiveIndex])
      .filter((group): group is ImportedModelGeometryGroup => group !== undefined)

    if (selectedGroups.length === 0) {
      return null
    }

    if (selectedGroups.length === groups.length) {
      return geometry.clone()
    }

    const subsetGeometry = new THREE.BufferGeometry()
    const indexAttribute = geometry.getIndex()

    if (indexAttribute) {
      const originalIndices: number[] = []
      selectedGroups.forEach((group) => {
        for (let indexOffset = group.start; indexOffset < group.start + group.count; indexOffset += 1) {
          originalIndices.push(indexAttribute.getX(indexOffset))
        }
      })

      const usedVertexIndices: number[] = []
      const vertexRemap = new Map<number, number>()
      const subsetIndices = originalIndices.map((vertexIndex) => {
        const existing = vertexRemap.get(vertexIndex)
        if (existing !== undefined) {
          return existing
        }

        const nextIndex = usedVertexIndices.length
        usedVertexIndices.push(vertexIndex)
        vertexRemap.set(vertexIndex, nextIndex)
        return nextIndex
      })

      Object.keys(geometry.attributes).forEach((attributeName) => {
        subsetGeometry.setAttribute(
          attributeName,
          this.sliceBufferAttributeByVertexIndices(
            geometry.getAttribute(attributeName),
            usedVertexIndices,
          ),
        )
      })
      subsetGeometry.setIndex(subsetIndices)
      return subsetGeometry
    }

    Object.keys(geometry.attributes).forEach((attributeName) => {
      subsetGeometry.setAttribute(
        attributeName,
        this.sliceBufferAttributeByGroups(geometry.getAttribute(attributeName), selectedGroups),
      )
    })
    subsetGeometry.setDrawRange(
      0,
      selectedGroups.reduce((count, group) => count + group.count, 0),
    )

    return subsetGeometry
  }

  /**
   * 收集 overlay 自己创建的材质。
   *
   * @param overlay 当前 overlay 对象。
   * @returns 待释放材质列表。
   */
  private collectOverlayMaterials(overlay: THREE.Mesh | THREE.Line | THREE.Sprite): THREE.Material[] {
    const material = overlay.material
    return Array.isArray(material) ? [...material] : [material]
  }

  /**
   * 创建 imported-model 点 overlay 共用的圆形精灵材质模板。
   *
   * 设计说明：
   * 1. 与 PointsMaterialFactory / datum-slot 原点标记保持一致，使用 shapeCircle 圆形遮罩；
   * 2. 禁止使用 THREE.SpriteMaterial，否则 WebGPU 下会显示为方形平面；
   * 3. 通道持有一份配方/投影状态模板，每个当前覆盖 Sprite 持有可独立 dispose 的同配方材质。
   *
   * @param color 点高亮颜色。
   * @returns 圆形点精灵材质模板。
   */
  private static createPointOverlaySpriteMaterial(color: THREE.ColorRepresentation): CameraSpriteNodeMaterial {
    const material = new CameraSpriteNodeMaterial({
      sizeAttenuation: false,
      alphaToCoverage: true,
      transparent: true,
      opacityNode: shapeCircle(),
      color,
    })
    material.transparent = true
    material.opacity = 0.95
    material.depthTest = false
    material.depthWrite = false
    return material
  }

  /**
   * 延迟释放 overlay 临时资源。
   *
   * 设计说明：
   * WebGPU 渲染器可能在事件回调清理 overlay 后仍提交上一帧 command buffer。
   * 如果同步 dispose subset geometry，浏览器会报 `setIndexBuffer` 收到失效 GPUBuffer。
   * 因此这里先把 overlay 从场景移除，再把本 renderer 创建的资源延后到后续帧释放。
   *
   * @param geometries 待释放几何。
   * @param materials 待释放材质。
   */
  private scheduleOverlayResourcesDisposal(
    geometries: readonly THREE.BufferGeometry[],
    materials: readonly THREE.Material[],
  ): void {
    const disposeResources = (): void => {
      geometries.forEach((geometry) => geometry.dispose())
      materials.forEach((material) => material.dispose())
    }

    const requestFrame = globalThis.requestAnimationFrame
    if (typeof requestFrame === 'function') {
      requestFrame(() => {
        requestFrame(disposeResources)
      })
      return
    }

    const setTimer = globalThis.setTimeout
    if (typeof setTimer === 'function') {
      setTimer(disposeResources, 0)
      return
    }

    disposeResources()
  }

  /**
   * 按 groups 切片 buffer attribute。
   *
   * @param attribute 源 attribute。
   * @param groups 目标 groups。
   * @returns 切片后的 attribute。
   */
  private sliceBufferAttributeByGroups(
    attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
    groups: Array<Pick<ImportedModelGeometryGroup, 'start' | 'count'>>,
  ): THREE.BufferAttribute {
    const itemIndices: number[] = []
    groups.forEach((group) => {
      for (let itemIndex = group.start; itemIndex < group.start + group.count; itemIndex += 1) {
        itemIndices.push(itemIndex)
      }
    })

    return this.sliceAttributeByItemIndices(attribute, itemIndices)
  }

  /**
   * 按 vertex 下标切片 buffer attribute。
   *
   * @param attribute 源 attribute。
   * @param vertexIndices 顶点下标。
   * @returns 切片后的 attribute。
   */
  private sliceBufferAttributeByVertexIndices(
    attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
    vertexIndices: number[],
  ): THREE.BufferAttribute {
    return this.sliceAttributeByItemIndices(attribute, vertexIndices)
  }

  /**
   * 按 item 下标切片 attribute。
   *
   * @param attribute 源 attribute。
   * @param itemIndices item 下标。
   * @returns 新 attribute。
   */
  private sliceAttributeByItemIndices(
    attribute: THREE.BufferAttribute | THREE.InterleavedBufferAttribute,
    itemIndices: number[],
  ): THREE.BufferAttribute {
    const itemSize = attribute.itemSize
    const sourceArray =
      attribute instanceof THREE.InterleavedBufferAttribute
        ? attribute.data.array
        : attribute.array
    const stride =
      attribute instanceof THREE.InterleavedBufferAttribute ? attribute.data.stride : itemSize
    const baseOffset = attribute instanceof THREE.InterleavedBufferAttribute ? attribute.offset : 0
    const ArrayCtor = sourceArray.constructor as {
      new(length: number): {
        [index: number]: number
        length: number
      }
    }
    const targetArray = new ArrayCtor(itemIndices.length * itemSize)
    let writeOffset = 0

    itemIndices.forEach((itemIndex) => {
      const sourceOffset = itemIndex * stride + baseOffset
      for (let componentIndex = 0; componentIndex < itemSize; componentIndex += 1) {
        targetArray[writeOffset] = sourceArray[sourceOffset + componentIndex] ?? 0
        writeOffset += 1
      }
    })

    return new THREE.BufferAttribute(targetArray as never, itemSize, attribute.normalized)
  }

  /**
   * 在误用 dispose 后给出明确错误。
   */
  private ensureNotDisposed(): void {
    if (this.disposed) {
      throw new Error('ImportedModelOverlayRenderer 已释放，无法继续渲染 overlay')
    }
  }
}
